import { useCallback } from 'react';
import { gql } from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { ResourceRequestStatus } from '~/types';
import { specificResourceAllocationFragment } from '~/modules/resourcing/common/fragments';
import {
  tryResourcePlanAllocatedUsersAndRolesCachedQuery,
  GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES
} from '~/modules/projects/resourcing-plan/ResourceRequestChart/hooks/useFetchAllocatedUsersAndRoles';
import { GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES_VARIABLES } from '~/modules/projects/resourcing-plan/ResourceRequestChart/hooks/useFetchResourcePlanUsersAndRoles';
import { getSessionStorage } from '~/modules/common/hooks';
import { getUpdatedAllocatedUserListFromAllocations } from '~/modules/resourcing/common/util/allocationCacheUpdates';

export const BULK_MARK_RESOURCE_REQUESTS_AS_COMPLETE = gql`
  mutation bulkMarkResourceRequestsAsComplete(
    $input: BulkMarkResourceRequestsAsCompleteInput!
  ) {
    bulkMarkResourceRequestsAsComplete: bulkMarkResourceRequestsAsComplete2(
      input: $input
    ) {
      resourceRequests {
        id
        requestStatus
        resourceAllocations {
          ...SpecificResourceAllocation
        }
      }
      isOptimistic @client
    }
  }
  ${specificResourceAllocationFragment}
`;

export const updateCachedAllocationDataOnResourcePlanPage = ({
  proxy,
  allocationChartRef,
  resourceAllocations
}) => {
  const newUsers = resourceAllocations.map(allocation => allocation.user);

  const variables = getSessionStorage(
    GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES_VARIABLES,
    null
  );

  const cachedResults = tryResourcePlanAllocatedUsersAndRolesCachedQuery({
    proxy,
    variables
  });

  const userList = cachedResults
    ? getUpdatedAllocatedUserListFromAllocations({
        newAllocations: resourceAllocations,
        cachedAllocatedUsers:
          cachedResults.project.resourcePlanAllocatedUsersAndRoles
      })
    : [];

  proxy.writeQuery({
    query: GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES,
    variables,
    data: {
      project: {
        ...cachedResults?.project,
        resourcePlanAllocatedUsersAndRoles: userList
      }
    }
  });

  allocationChartRef?.current?.handleResourceAllocationUserChange({
    newUsers: newUsers.map(user => user.user)
  });
  allocationChartRef?.current?.handleLoadingStateOnResourceRequestAccept({
    newUsers
  });
};

export const updateCacheForProjectResourcePlanPage = ({
  allocationChartRef
}) => (proxy, mutationResponse) => {
  const {
    data: {
      bulkMarkResourceRequestsAsComplete: { isOptimistic, resourceRequests }
    }
  } = mutationResponse;
  const resourceAllocations = resourceRequests.flatMap(
    entry => entry.resourceAllocations || []
  );

  if (isOptimistic || resourceAllocations?.length === 0) return;

  updateCachedAllocationDataOnResourcePlanPage({
    proxy,
    allocationChartRef,
    resourceAllocations
  });
};

export const useBulkMarkResourceRequestsAsComplete = () => {
  const [bulkMarkResourceRequestsAsComplete] = useMutation(
    BULK_MARK_RESOURCE_REQUESTS_AS_COMPLETE
  );

  const bulkMarkResourceRequestsAsCompleteCallback = useCallback(
    ({ proposedResourceRequests, allocationChartRef }) => {
      const optimisticResponse = {
        __typename: 'Mutation',
        bulkMarkResourceRequestsAsComplete: {
          __typename: 'BulkMarkResourceRequestsAsCompleteResult',
          isOptimistic: true,
          resourceRequests: proposedResourceRequests.map(resourceRequest => ({
            __typename: 'ResourceRequest',
            ...resourceRequest,
            requestStatus: ResourceRequestStatus.Complete
          }))
        }
      };

      return bulkMarkResourceRequestsAsComplete({
        variables: {
          input: {
            resourceRequestIds: proposedResourceRequests.map(
              resourceRequest => resourceRequest.id
            )
          }
        },
        refetchQueries: ['resourceRequestWithTimeoff'],
        optimisticResponse,
        update: updateCacheForProjectResourcePlanPage({ allocationChartRef })
      });
    },
    [bulkMarkResourceRequestsAsComplete]
  );

  return {
    bulkMarkResourceRequestsAsComplete: bulkMarkResourceRequestsAsCompleteCallback
  };
};

export default useBulkMarkResourceRequestsAsComplete;
